import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import API from 'api';
import jwtDecode from 'jwt-decode';
import { readCachedSaviAuthToken } from 'utils/saviAuthTokenStorage';

// buffer before expiry to trigger a refresh, matching useRefreshFirebaseToken's buffer
const TOKEN_REFRESH_BUFFER_MS = 1000 * 60 * 10; // 10 minutes

export const useSaviAuthToken = () => {
  const { getAccessTokenSilently } = useAuth0();

  const getAuth0UserId = useCallback(async () => {
    const auth0Token = await getAccessTokenSilently();
    return auth0Token && jwtDecode<{ sub: string }>(auth0Token).sub;
  }, [getAccessTokenSilently]);

  // returns a savi auth token that's valid for at least TOKEN_REFRESH_BUFFER_MS, refreshing it
  // first if the cached one is missing, expired, or expiring soon. Safe to call both up-front
  // when a flow starts (e.g. entering check-in) and again right before the token is actually
  // used (e.g. tokenising a link), so a stale cached token is never used.
  const getFreshSaviAuthToken = useCallback(async () => {
    const auth0UserId = await getAuth0UserId();
    if (!auth0UserId) return '';

    const cached = readCachedSaviAuthToken(auth0UserId);
    const tokenValid = !!cached && cached.expiryEpochMs - Date.now() > TOKEN_REFRESH_BUFFER_MS;
    if (tokenValid) return cached.token;

    // this fetches a fresh savi auth token and caches it to storage
    const user = await API.users.getCurrentUser(auth0UserId);
    return user.saviAuthToken;
  }, [getAuth0UserId]);

  return { getFreshSaviAuthToken };
};
